import React, { useEffect, useState } from 'react';
import { SignInButton, SignUpButton } from '@clerk/clerk-react';
import { ArrowRight, ClockCounterClockwise, FilmReel, Sparkle, UploadSimple } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

interface LandingPageProps {
  clerkEnabled: boolean;
}

const useIsMobile = (breakpoint = 900): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(media.matches);

    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [breakpoint]);

  return isMobile;
};

const LandingPage: React.FC<LandingPageProps> = ({ clerkEnabled }) => {
  const isMobile = useIsMobile();

  return (
    <div className="landing-page">
      <header className="landing-nav">
        <div className="brand-mark">
          <span>VidInsight</span>
          <small>Case Studies for Explainer Agencies</small>
        </div>

        <div className="nav-actions">
          <a href="#workflow" className="ghost-link">
            How It Works
          </a>

          {clerkEnabled ? (
            isMobile ? (
              <Link to="/login" className="ghost-link">
                Log In
              </Link>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/app">
                <button type="button" className="ghost-link as-button">
                  Log In
                </button>
              </SignInButton>
            )
          ) : (
            <Link to="/app" className="ghost-link">
              Open App
            </Link>
          )}
        </div>
      </header>

      <section className="hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">Creative Ops for Agency Owners</p>
          <h1>Turn Every Explainer Video Into a High-Trust Case Study</h1>
          <p>
            Upload a finished video or paste a YouTube link. VidInsight generates structured case-study
            drafts you can directly reuse on your website, proposals, and sales decks.
          </p>

          <div className="hero-cta-row">
            {clerkEnabled ? (
              isMobile ? (
                <Link to="/signup" className="primary-cta">
                  Start Free <ArrowRight size={16} aria-hidden="true" />
                </Link>
              ) : (
                <SignUpButton mode="modal" forceRedirectUrl="/app">
                  <button type="button" className="primary-cta as-button">
                    Start Free <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </SignUpButton>
              )
            ) : (
              <Link to="/app" className="primary-cta">
                Open Workspace <ArrowRight size={16} aria-hidden="true" />
              </Link>
            )}

            <a href="#workflow" className="secondary-cta">
              See Workflow
            </a>
          </div>

          <p className="hero-footnote">
            Built for 1-5 minute explainer videos. No technical setup required to start.
          </p>
        </div>

        <div className="hero-panel">
          <div className="status-pill">
            <Sparkle size={14} weight="duotone" aria-hidden="true" />
            Agency-grade output, client-ready narrative
          </div>

          <div className="panel-card">
            <h3>What you get in each run</h3>
            <ul>
              <li>Story summary + positioning insights</li>
              <li>Case-study structure (challenge, strategy, outcome)</li>
              <li>Reusable copy blocks for website/collateral</li>
              <li>Saved history for future reuse</li>
            </ul>
          </div>

          <div className="panel-note">
            BYOK is optional. Most users start with app credits and add BYOK later if they need higher
            volume.
          </div>
        </div>
      </section>

      <section id="workflow" className="workflow-section">
        <header>
          <p className="eyebrow">Fast, guided workflow</p>
          <h2>From Video Link to Case Study Draft in Minutes</h2>
        </header>

        <div className="workflow-grid">
          <article>
            <div className="step-icon">
              <FilmReel size={18} aria-hidden="true" />
            </div>
            <h3>1. Add Your Video</h3>
            <p>Paste a YouTube URL or upload an MP4/WebM file from your portfolio.</p>
          </article>

          <article>
            <div className="step-icon">
              <ClockCounterClockwise size={18} aria-hidden="true" />
            </div>
            <h3>2. Guided Processing</h3>
            <p>Watch stage-by-stage progress with clear status and retry guidance when needed.</p>
          </article>

          <article>
            <div className="step-icon">
              <UploadSimple size={18} aria-hidden="true" />
            </div>
            <h3>3. Publish-Ready Output</h3>
            <p>Use the generated case-study draft for your website sections and sales collateral.</p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
